// @boty/ai - Chat response generation
// Generates streaming chat responses based on bot content

import { getOpenAIClient, AI_MODELS, handleOpenAIError } from './provider';
import type { AIUsageCallback } from './types';

/**
 * Message structure for chat history
 */
export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

/**
 * Content source for attribution tracking
 */
export interface ContentSource {
  name: string;
  type: 'text' | 'file' | 'url' | 'image' | 'google_sheets' | 'google_docs' | 'clarification';
  content: string;
}

/**
 * Options for generating chat response
 */
export interface ChatResponseOptions {
  /** Combined bot content for context */
  content: string;
  /** Content sources for attribution */
  sources?: ContentSource[];
  /** User's question */
  question: string;
  /** Conversation history (last N messages) */
  history?: ChatMessage[];
  /** Maximum tokens for response (default: 2500 — gpt-5 reasoning tokens count against this) */
  maxTokens?: number;
  /** Optional callback for reporting LLM usage data */
  onUsage?: AIUsageCallback;
}

/**
 * System prompt for chat responses
 * Designed to:
 * - Only answer from provided content
 * - Admit when information is unavailable
 * - Match response language to question language (CRITICAL)
 * - Be friendly and conversational
 */
const CHAT_SYSTEM_PROMPT = `You are a helpful assistant for a chatbot. Answer questions ONLY based on the content provided below.

CONTENT:
{content}

CRITICAL - LANGUAGE MATCHING:
The content above may be in ANY language. You MUST understand and use ALL content regardless of its language when answering.
You MUST respond in the SAME language as the user's question.
- If the question is in Hebrew → respond entirely in Hebrew, even if the content is in English
- If the question is in English → respond entirely in English, even if the content is in Hebrew
- If the question mixes languages → respond in the dominant language
- Translate information from the content into the question's language as needed. Preserve URLs, brand names, and technical terms in their original form.

IMPORTANT RULES:
1. ONLY answer based on the content above. Never make up information.
2. If the question cannot be answered from the content, say so in the question's language:
   - English: "I don't have information about that based on what's been shared with me."
   - Hebrew: "אין לי מידע על כך לפי מה שנמסר לי."
   Then briefly mention what type of information would be needed to answer.
3. Keep responses concise but helpful (2-4 sentences for simple questions).
4. If the content contains contact info, hours, pricing - include them when relevant.
5. Be friendly and conversational. When the visitor asks about something the creator offers, sells, teaches, or invites them to do, you MAY add at most ONE short phrase of warmth (e.g. "Happy you're interested", "Glad you asked", "I think you'll find this useful"). Rules: use sparingly (not every reply, never stacked), only on offering/invitation questions — never on factual ones (hours, price, address), or it sounds salesy and erodes trust. Translate to the visitor's language (Hebrew: "שמח/ה שאת/ה מתעניין/ת"; Spanish: "Me alegra que preguntes").
6. If you quote or reference specific information, note the source (e.g., "Based on the schedule..." or "From the pricing section...").
7. Do NOT say "based on the content provided" or similar meta-phrases. Just answer naturally.
8. If the user's input is a conversational acknowledgement, exclamation, or non-question (e.g. "thanks", "cool", "I'm excited", "I'd love to go", "תודה", "מגניב", "אני מתרגש", "gracias", "qué bien"), reply with ONE short, warm, contextual sentence. DO NOT repeat any answer you already gave; DO NOT dump address/pricing/hours again unless the user explicitly asks. If unsure whether the input is a question, treat anything under ~4 words with no question word/mark as conversational.
9. NEVER restate, summarize, or include the content of an earlier reply in your current reply. The user has already read it. Address ONLY the current question. If asked about pricing, answer pricing — do not also re-state location, hours, or anything else from prior turns.`;

/**
 * Builds the system prompt with content
 */
function buildSystemPrompt(content: string): string {
  return CHAT_SYSTEM_PROMPT.replace('{content}', content);
}

/**
 * Truncates conversation history to fit within token limits
 * Keeps the most recent messages
 * @param history - Full conversation history
 * @param maxMessages - Maximum messages to keep (default: 10)
 * @returns Truncated history
 */
export function truncateHistory(
  history: ChatMessage[],
  maxMessages: number = 10
): ChatMessage[] {
  if (history.length <= maxMessages) {
    return history;
  }
  // Keep the most recent messages
  return history.slice(-maxMessages);
}

/**
 * Generates a streaming chat response
 * @param options - Chat response options
 * @returns AsyncIterable of content chunks
 */
export async function* generateChatResponseStream(
  options: ChatResponseOptions
): AsyncIterable<string> {
  const { content, question, history = [], maxTokens = 2500, onUsage } = options;

  const client = getOpenAIClient();
  const systemPrompt = buildSystemPrompt(content);
  const startTime = Date.now();

  // Build messages array
  const messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }> = [
    { role: 'system', content: systemPrompt },
    ...truncateHistory(history),
    { role: 'user', content: question },
  ];

  try {
    const stream = await client.chat.completions.create({
      model: AI_MODELS.GPT5_4_MINI,
      messages,
      // gpt-5 models require max_completion_tokens (not max_tokens) and only
      // accept the default temperature of 1. reasoning_effort: 'low' keeps
      // the hidden reasoning tokens small while still handling the system
      // prompt's compound rules (language match, factual-vs-offering tone,
      // scope detection). gpt-5.4-mini doesn't accept 'minimal' — it 400s.
      max_completion_tokens: maxTokens,
      reasoning_effort: 'low',
      stream: true,
      stream_options: { include_usage: true },
    });

    let usage:
      | { prompt_tokens: number; completion_tokens: number; prompt_tokens_details?: { cached_tokens?: number } }
      | undefined;
    let outputText = '';

    for await (const chunk of stream) {
      const delta = chunk.choices[0]?.delta?.content;
      if (delta) {
        yield delta;
        if (outputText.length < 1000) outputText += delta;
      }
      if (chunk.usage) {
        usage = chunk.usage;
      }
    }

    if (onUsage && usage) {
      try {
        onUsage({
          model: AI_MODELS.GPT5_4_MINI,
          provider: 'openai',
          inputTokens: usage.prompt_tokens,
          ...(usage.prompt_tokens_details?.cached_tokens !== undefined && {
            cachedInputTokens: usage.prompt_tokens_details.cached_tokens,
          }),
          outputTokens: usage.completion_tokens,
          latencyMs: Date.now() - startTime,
          functionName: 'generateChatResponseStream',
          inputMessages: [{ role: 'user', content: question }],
          ...(outputText && { outputText }),
        });
      } catch { /* never break the call */ }
    }
  } catch (error) {
    handleOpenAIError(error, 'generateChatResponse');
  }
}

/**
 * Generates a non-streaming chat response (for testing)
 * @param options - Chat response options
 * @returns Complete response text
 */
export async function generateChatResponse(
  options: ChatResponseOptions
): Promise<string> {
  let fullResponse = '';
  for await (const chunk of generateChatResponseStream(options)) {
    fullResponse += chunk;
  }
  return fullResponse;
}

/**
 * Checks if a response indicates the bot couldn't answer
 * Used to trigger "add more content" prompt
 */
export function isOutOfScopeResponse(response: string): boolean {
  const indicators = [
    "i don't have information",
    "i don't have that information",
    "isn't included in",
    "wasn't shared with me",
    "not mentioned in",
    "no information about",
    "cannot answer",
    "can't answer",
    "אין לי מידע",
    "לא נמסר לי",
    "לא מצוין",
  ];

  const lowerResponse = response.toLowerCase();
  return indicators.some((indicator) => lowerResponse.includes(indicator));
}
